import compose from "@hypnosphi/recompose/compose";
import { withStyles } from "@mui/styles";
import { useState, useRef, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";

import MenuItem from "apollo-react/components/MenuItem";
import Typography from "apollo-react/components/Typography";
import Select from "apollo-react/components/Select";
import TextField from "apollo-react/components/TextField";
import Tooltip from "apollo-react/components/Tooltip";
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';

import { secondaryOrange } from 'apollo-react-next/colors';
import { usePostmongerContext } from "../../contexts/PostmongerContext";

import Traveler from "./Traveler";
import Webhook from "./Webhook";
import OCEPersonal from "./OCEPersonal";
import FTPFileUpload from "./FTPFileUpload";
import Event from "./Event";
import GenericButtons from "./GenericButtons";
import { featureFlags } from "../common/constants";

const styles = (theme) => ({
  paper: {
    maxWidth: 800,
    padding: 32,
    "& label": {
      marginBottom: 0,
    },
  },
  submit: {
    margin: "16px 0",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  section: {
    marginBottom: 32,
  },
  warning: {
    color: "#E20000",
  },
  warningIcon: {
    color: secondaryOrange[500],
    marginRight: '5px',
  },
  warningMessage: {
    display: 'flex',
    alignItems: 'center',
    textTransform: 'none',
    fontWeight: 'normal',
    fontSize: 'small',
    marginTop: '15px',
  },
});

export const getDefaultNodeType = (isHCOJourney, inArguments) => isHCOJourney ? "FTPFileUpload" : inArguments.get("nodeType") || "Webhook";

export const displayErrorMsg = (seedData, isHCOJourney, isNotDraft) => {
  if (isNotDraft || (seedData && seedData.isHCOEnabled === undefined)) {
    return false;
  }
  else
  {
    return !(seedData && seedData.isHCOEnabled) && isHCOJourney
  }
  
} 

export const isValidTagRefWithPage = (TagRef, page) => {
  return !(TagRef && TagRef.current && TagRef.current.isValid && TagRef.current.isValid(page))
}

export const isValidTagRef = (TagRef) => {
  return !(TagRef && TagRef.current && TagRef.current.isValid &&  TagRef.current.isValid())
}

export const GenericNode = ({ classes }) => {
  const { t } = useTranslation();

  const {
    isLocalhost,
    inArguments,
    signalDone,
    signalCancelled,
    setDirty,
    seedData,
    disabled,
    noTopicAvailable,
  } = usePostmongerContext();

  const isHCOJourney = seedData && seedData.Metadata && seedData.Metadata.IsHCO === true ? true : false;
  const showError = displayErrorMsg(seedData, isHCOJourney, disabled);
  const enableTravelersNode = seedData && seedData.Traveler && seedData.Traveler.isDevelopment
    ? seedData.Traveler.isDevelopment : isLocalhost;
  const enableOCEPersonal = seedData && seedData.OCEPersonal && seedData.OCEPersonal.salesConnectionExists
    ? seedData.OCEPersonal.salesConnectionExists : false;

  const nodeTypeComponents = {
    FTPFileUpload: {
      node: FTPFileUpload,
      ref: useRef(FTPFileUpload),
      enabled: true,
      pages: 3,
    },
    "OCE Personal": {
      node: OCEPersonal,
      ref: useRef(OCEPersonal),
      enabled:
      isHCOJourney ? false : enableOCEPersonal,
    },
    Traveler: {
      node: Traveler,
      ref: useRef(Traveler),
      enabled:
      isHCOJourney ? false : enableTravelersNode,
    },
    Webhook: {
      node: Webhook,
      ref: useRef(Webhook),
      enabled: !isHCOJourney,
    },
    Event: {
      node: Event,
      ref: useRef(Event),
      enabled: !isHCOJourney,
    },
  };

  const GetMenuItems = () => {
    const items = [];
    const keys = Object.keys(nodeTypeComponents);
    for (let item of keys) {
      const node = nodeTypeComponents[item];

      if (node.enabled) {
        items.push(
          <MenuItem key={item} value={item}>
            {t(item)}
          </MenuItem>
        );
      }
    }
    return items;
  };

  const [nodeTypeMenuItems, setNodeTypeMenuItems] = useState(GetMenuItems());

  const defaultNodeType = getDefaultNodeType(isHCOJourney, inArguments);
  const defaultDescription = inArguments.get("description");

  const [nodeType, setNodeType] = useState(defaultNodeType);
  const [description, setDescription] = useState(defaultDescription);
  const [page, setPage] = useState(0);
  const [pageCount, setPageCount] = useState(1);

  const setupPages = (nodeType) => {
    setPage(1);
    setPageCount(nodeTypeComponents[nodeType].pages || 1);
  };

  const menuCallback = useCallback((name, enabled) => {
    nodeTypeComponents[name].enabled = enabled;
    setNodeTypeMenuItems(GetMenuItems());
    setupPages(name);
  }, []);

  const TagName = nodeTypeComponents[nodeType].node;
  const TagRef = nodeTypeComponents[nodeType].ref;

  

  const validateSelectedChannel = (nodeType) => {
    if (nodeType == "FTPFileUpload") {
      //do we have a channel selected inArguments?  If so, validate it against our current channel list.
      if (
        seedData &&
        seedData.FTPFileUpload &&
        inArguments.get("channelCode") &&
        inArguments.get("channelCode") !== "None"
      ) {
        if (isValidTagRefWithPage(TagRef,page)) {
          return;
        }
      }
    } else if (nodeType == "Webhook") {
      //do we have a channel selected inArguments?  If so, validate it against our current channel list.
      if (
        seedData &&
        seedData.Webhook &&
        inArguments.get("webhook.Channel") &&
        inArguments.get("webhook.Channel") !== ""
      ) {
        if (isValidTagRef(TagRef)) {
          return;
        }
      }
    }
  };

  useEffect(() => {
    setupPages(nodeType);
    setNodeTypeMenuItems(GetMenuItems());
    validateSelectedChannel(nodeType);
  }, [seedData]);

  if (nodeType != defaultNodeType) {
    setNodeType(defaultNodeType);
    setupPages(defaultNodeType);
  }

  if (defaultDescription != description) {
    setDescription(defaultDescription);
  }

  const onDoneButtonClick = () => {
    if (isValidTagRefWithPage(TagRef,page)) {
      return;
    }
    signalDone();
  };

  const onCancelButtonClick = () => signalCancelled();

  const onNodeTypeSelectChange = (e) => {
    const value = e.target.value;
    setupPages(value);
    setNodeType(value);
    inArguments.set("nodeType", value);
    setDirty();
  };

  const onDescriptionChange = (e) => {
    setDescription(e.target.value);
    inArguments.set("description", e.target.value);
    setDirty();
  };

  const onPreviousClick = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const onNextClick = () => {
    if (page < pageCount) {
      if (isValidTagRefWithPage(TagRef,page)) {
        return;
      }
      setPage(page + 1);
    }
  };
  return (
    
    <>{
        noTopicAvailable ? (
        <div className={classes.section}>
          <div>{t("generic.noTopicAvailable")}</div>
          <GenericButtons
            page={page}
            pageCount={pageCount}
            onDone={onDoneButtonClick}
            onCancel={onCancelButtonClick}
          />
        </div>
      ) : (
        
        <>
        { showError && (
          <div className={classes.section}>
          <div>{t("generic.errorConfiguration")}</div>
          <GenericButtons
            page={page}
            pageCount={pageCount}
            onDone={onDoneButtonClick}
            onCancel={onCancelButtonClick}
          />
        </div>) }{ !showError && (<>
          <div className={classes.section}>
            <Typography variant="title1">{t("generic.title")}</Typography>
            <Select
              name="nodeType"
              label={t("generic.nodeTypeCaption")}
              value={nodeType}
              fullWidth
              required
              canDeselect={false}
              onChange={onNodeTypeSelectChange}
              disabled={disabled}
              helperText={
                nodeType === "Event" &&
                seedData.featureFlags &&
                !seedData.featureFlags[featureFlags.WEBINARS] && (
                  <span className={classes.warning}>
                    {t("orchestrationType_Event_NoWebinar")}
                  </span>
                )
              }
            >
              {nodeTypeMenuItems}
            </Select>
            <Tooltip title={t("generic.activityNameTooltip")}>
              <TextField
                fullWidth
                label={t("generic.activityNameCaption")}
                value={description}
                disabled={disabled}
                onChange={onDescriptionChange}
              />
            </Tooltip>
          </div>
          <TagName ref={TagRef} menuCallback={menuCallback} page={page} />
          <GenericButtons
            page={page}
            pageCount={pageCount}
            onPrevious={onPreviousClick}
            onNext={onNextClick}
            onDone={onDoneButtonClick}
            onCancel={onCancelButtonClick}
          />
          </>)}
        </>
      )}
    </>
  );
};

export default compose(withStyles(styles))(GenericNode);
